import { useState, useRef } from "react";
import Swal from "sweetalert2";

interface FormData {
  empresa: string;
  nombre: string;
  apellido: string;
  email: string;
  telefono: string;
  asunto: string;
  descripcion: string;
}

interface FormErrors {
  empresa?: string;
  nombre?: string;
  apellido?: string;
  email?: string;
  telefono?: string;
  asunto?: string;
  descripcion?: string;
}

export const useFormSubmit = () => {
  const [formData, setFormData] = useState<FormData>({
    empresa: "",
    nombre: "",
    apellido: "",
    email: "",
    telefono: "",
    asunto: "",
    descripcion: "",
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useRef<HTMLFormElement>(null);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    return phoneRegex.test(phone);
  };

  const formatPhoneNumber = (value: string): string => {
    let phoneNumber = value.replace(/[^0-9]/g, "");
    phoneNumber = phoneNumber.substring(0, 10);

    if (phoneNumber.length <= 3) {
      return `(${phoneNumber}`;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(3)}`;
    } else {
      return `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(
        3,
        6
      )}-${phoneNumber.substring(6)}`;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.nombre.trim()) {
      newErrors.nombre = "El nombre es requerido";
    }

    if (!formData.apellido.trim()) {
      newErrors.apellido = "El apellido es requerido";
    }

    if (!formData.email.trim()) {
      newErrors.email = "El email es requerido";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "El email no es válido";
    }

    if (!formData.telefono.trim()) {
      newErrors.telefono = "El teléfono es requerido";
    } else if (!validatePhone(formData.telefono)) {
      newErrors.telefono = "El teléfono debe tener el formato (XXX) XXX-XXXX";
    }

    if (!formData.asunto.trim()) {
      newErrors.asunto = "El asunto es requerido";
    }

    if (!formData.descripcion.trim()) {
      newErrors.descripcion = "La descripción es requerida";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "telefono") {
      const formattedPhone = formatPhoneNumber(value);
      setFormData((prev) => ({ ...prev, [name]: formattedPhone }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const enviarEmail = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        title: "Error",
        text: "Por favor, corrige los errores en el formulario",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Crear un FormData para enviar al endpoint de FormSubmit
      const formDataToSend = new FormData();

      // Agregar todos los campos del formulario
      formDataToSend.append("empresa", formData.empresa);
      formDataToSend.append("nombre", formData.nombre);
      formDataToSend.append("apellido", formData.apellido);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("telefono", formData.telefono);
      formDataToSend.append("asunto", formData.asunto);
      formDataToSend.append("descripcion", formData.descripcion);

      // Configuraciones adicionales de FormSubmit
      formDataToSend.append(
        "_subject",
        `Nuevo mensaje de contacto: ${formData.asunto}`
      );
      formDataToSend.append("_replyto", formData.email);
      formDataToSend.append("_next", window.location.origin + "/?success=true");
      formDataToSend.append("_captcha", "false"); // Deshabilitar captcha para mejor UX

      // Enviar el formulario usando fetch
      const response = await fetch(
        "https://formsubmit.co/comunicaciones@penadefillo.com",
        {
          method: "POST",
          body: formDataToSend,
        }
      );

      if (response.ok) {
        // Limpiar formulario
        setFormData({
          empresa: "",
          nombre: "",
          apellido: "",
          email: "",
          telefono: "",
          asunto: "",
          descripcion: "",
        });

        Swal.fire({
          title: "¡Exitoso!",
          text: "Su mensaje ha sido enviado correctamente!",
          icon: "success",
          confirmButtonText: "Aceptar",
        });
      } else {
        throw new Error("Error en el servidor");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      Swal.fire({
        title: "Error",
        text:
          "Hubo un error al enviar el mensaje. Por favor, inténtalo de nuevo.",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    errors,
    isSubmitting,
    form,
    handleInputChange,
    enviarEmail,
  };
};
